import React, { useState, useEffect, useRef } from "react";

interface AnalyzingScreenProps {
  photoUrls: string[];
  onComplete: () => void;
}

const analysisSteps = [
  "Загрузка изображения…",
  "Определение пропорций тела…",
  "Анализ черт лица…",
  "Оценка структуры костяка…",
  "Определение баланса линий…",
  "Классификация типажа…",
  "Подготовка рекомендаций…",
];

export function AnalyzingScreen({ photoUrls, onComplete }: AnalyzingScreenProps) {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);

  // Use a ref for onComplete so the timer effect doesn't re-run when the callback changes
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Cycle through ALL uploaded photos rapidly for dynamic scanning effect
  useEffect(() => {
    if (photoUrls.length <= 1) return;
    const photoInterval = setInterval(() => {
      setCurrentPhotoIndex((prev) => (prev + 1) % photoUrls.length);
    }, 250); // Fast 250ms rotation for dynamic effect
    return () => clearInterval(photoInterval);
  }, [photoUrls.length]);

  useEffect(() => {
    const totalDuration = 4000;
    const stepDuration = totalDuration / analysisSteps.length;

    const stepInterval = setInterval(() => {
      setCurrentStep((prev) => {
        if (prev >= analysisSteps.length - 1) {
          clearInterval(stepInterval);
          return prev;
        }
        return prev + 1;
      });
    }, stepDuration);

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + 1;
      });
    }, totalDuration / 100);

    const timer = setTimeout(() => {
      try {
        onCompleteRef.current();
      } catch (err) {
        console.error("onComplete callback error:", err);
      }
    }, totalDuration + 600);

    // Safety net: if still mounted 8s after start, force-call onComplete again
    const safetyTimer = setTimeout(() => {
      try {
        onCompleteRef.current();
      } catch (err) {
        console.error("Safety timeout onComplete error:", err);
      }
    }, 8000);

    return () => {
      clearInterval(stepInterval);
      clearInterval(progressInterval);
      clearTimeout(timer);
      clearTimeout(safetyTimer);
    };
  }, []); // Run once on mount — uses ref for stable callback

  return (
    <div className="min-h-[100dvh] flex flex-col items-center justify-center px-6 relative overflow-hidden">
      {/* Soft background pulse */}
      <div
        className="absolute inset-0 animate-breathe"
        style={{
          background: "radial-gradient(circle at 50% 40%, var(--rose-mist) 0%, var(--background) 70%)",
        }}
      />

      <div className="relative z-10 flex flex-col items-center w-full max-w-[320px]">
        {/* Photo preview with scanning effect */}
        <div className="relative mb-10 animate-scale-in">
          <div
            className="w-[180px] h-[240px] rounded-[24px] overflow-hidden relative"
            style={{
              boxShadow: "0 20px 60px oklch(0.48 0.14 12 / 0.15)",
            }}
          >
            {photoUrls.map((url, i) => (
              <img
                key={i}
                src={url}
                alt={`Фото ${i + 1}`}
                className="absolute inset-0 w-full h-full object-cover transition-all duration-150 ease-in-out"
                style={{
                  opacity: i === currentPhotoIndex ? 1 : 0,
                  transform: i === currentPhotoIndex ? "scale(1)" : "scale(1.05)",
                }}
              />
            ))}

            {/* Overlay gradient */}
            <div
              className="absolute inset-0"
              style={{
                background: "linear-gradient(180deg, oklch(0.48 0.14 12 / 0.05) 0%, oklch(0.48 0.14 12 / 0.15) 100%)",
              }}
            />

            {/* Scan line */}
            <div
              className="absolute left-0 right-0 h-[2px] animate-scan"
              style={{
                background: "linear-gradient(90deg, transparent 0%, var(--rose) 50%, transparent 100%)",
                boxShadow: "0 0 20px var(--rose-light)",
              }}
            />

            {/* Corner brackets */}
            <svg className="absolute top-3 left-3 w-5 h-5" viewBox="0 0 20 20" fill="none">
              <path d="M1 7V2.5C1 1.67 1.67 1 2.5 1H7" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <svg className="absolute top-3 right-3 w-5 h-5" viewBox="0 0 20 20" fill="none">
              <path d="M19 7V2.5C19 1.67 18.33 1 17.5 1H13" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <svg className="absolute bottom-3 left-3 w-5 h-5" viewBox="0 0 20 20" fill="none">
              <path d="M1 13V17.5C1 18.33 1.67 19 2.5 19H7" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <svg className="absolute bottom-3 right-3 w-5 h-5" viewBox="0 0 20 20" fill="none">
              <path d="M19 13V17.5C19 18.33 18.33 19 17.5 19H13" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </div>

          {/* Ripple effect behind photo */}
          <div
            className="absolute inset-0 rounded-[24px] animate-ripple"
            style={{ border: "2px solid var(--rose-light)" }}
          />
        </div>

        {/* Status text */}
        <div className="text-center mb-8">
          <h2
            className="font-editorial text-[28px] tracking-[-0.01em] mb-3 animate-fade-in"
            style={{ fontWeight: 400, color: "var(--foreground)" }}
          >
            Анализируем
          </h2>
          <p
            className="font-body text-[14px] h-[20px] transition-all duration-500"
            style={{ color: "var(--muted-foreground)" }}
            key={currentStep}
          >
            <span className="animate-fade-in inline-block">{analysisSteps[currentStep]}</span>
          </p>
        </div>

        {/* Progress bar */}
        <div className="w-full">
          <div
            className="w-full h-[4px] rounded-full overflow-hidden"
            style={{ background: "var(--muted)" }}
          >
            <div
              className="h-full rounded-full transition-all duration-300 ease-out"
              style={{
                width: `${progress}%`,
                background: "linear-gradient(90deg, var(--rose) 0%, var(--mauve) 100%)",
              }}
            />
          </div>
          <div className="flex justify-between mt-2">
            <span
              className="font-body text-[12px]"
              style={{ color: "var(--muted-foreground)" }}
            >
              {analysisSteps[currentStep]}
            </span>
            <span
              className="font-body text-[12px] font-medium tabular-nums"
              style={{ color: "var(--rose)" }}
            >
              {progress}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
